import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MagnifyingGlassIcon, HouseIcon } from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import EmptyState from "../../components/EmptyState";
import SkeletonRows from "../../components/SkeletonRows";
import { ErrorBanner, Select } from "../../components/crm/Form";
import api, { formatApiError } from "../../lib/api";

const REGION_OPTIONS = [
  { value: "st_george", label: "St. George" },
  { value: "las_vegas", label: "Las Vegas" },
];

export default function PropertiesList() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const customerId = params.get("customer_id") || "";
  const region = params.get("region") || "";
  const initialQ = params.get("q") || "";

  const [list, setList] = useState([]);
  const [q, setQ] = useState(initialQ);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const filters = {};
      if (customerId) filters.customer_id = customerId;
      if (region) filters.region = region;
      if (q) filters.q = q;
      const { data } = await api.get("/properties/", { params: filters });
      setList(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, region]);

  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (q) next.set("q", q);
    else next.delete("q");
    setParams(next, { replace: true });
    load();
  };

  const setRegion = (val) => {
    const next = new URLSearchParams(params);
    if (val) next.set("region", val);
    else next.delete("region");
    setParams(next, { replace: true });
  };

  return (
    <Layout title="Properties" subtitle="CRM">
      <EntityHeader
        overline="addresses"
        title="Properties"
        subtitle="Every address Zion Solar services, organized by region."
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <form
          onSubmit={submitSearch}
          className="relative flex-1 min-w-[240px] max-w-md"
        >
          <MagnifyingGlassIcon
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search address…"
            data-testid="properties-search"
            className="w-full rounded-sm border border-slate-300 bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
          />
        </form>
        <div className="w-44">
          <Select
            value={region}
            onChange={(v) => setRegion(v)}
            options={REGION_OPTIONS}
            placeholder="All regions"
            data-testid="properties-region-filter"
          />
        </div>
      </div>

      <ErrorBanner message={err} />

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="properties-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-600">
              <th className="text-left px-5 py-3 font-bold">Address</th>
              <th className="text-left px-5 py-3 font-bold">ZIP</th>
              <th className="text-left px-5 py-3 font-bold">Region</th>
              <th className="text-left px-5 py-3 font-bold">Customer</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && list.length === 0 && <SkeletonRows count={5} cols={4} />}
            {!loading && list.length === 0 && (
              <EmptyState
                inTable
                colSpan={4}
                icon={HouseIcon}
                title={
                  q || region || customerId
                    ? "No matching properties"
                    : "No properties yet"
                }
                description={
                  q || region || customerId
                    ? "Try clearing the filters to see all properties."
                    : "Properties are added from a customer's detail page — open a customer and click 'Add Property'."
                }
                testId="properties-empty"
              />
            )}
            {list.map((p) => (
              <tr
                key={p.id}
                className="cursor-pointer hover:bg-slate-50/60"
                onClick={() => nav(`/properties/${p.id}`)}
                data-testid={`property-row-${p.id}`}
              >
                <td className="px-5 py-3 font-medium text-slate-900">
                  {p.address}
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-700">
                  {p.zip_code}
                </td>
                <td className="px-5 py-3 font-data text-xs uppercase tracking-[0.1em] text-slate-700">
                  {p.region.replace("_", " ")}
                </td>
                <td className="px-5 py-3 text-xs text-slate-700">
                  {p.customer_name || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
